// LoginPage — the unauthenticated portal entry point (RFC-0005 Task 3.2).
//
// Renders OUTSIDE the authenticated PortalLayout chrome (no sidebar/shell): it
// is the one page a signed-out visitor can reach. On mount it drives the Kratos
// self-service browser login flow through the same-origin `/kratos/*` proxy and
// renders the flow's Google OIDC control as a real top-level POST <form> to
// `ui.action` — a genuine navigation (not fetch) so the OIDC redirect chain
// works under SameSite=Lax and the session cookie lands on the portal origin.
//
// Scope (RFC-0005): sign-in only. It reads `return_to` from the query string
// and forwards it (as a sanitized relative path) so Kratos redirects there on
// success. It does NOT construct the `/login?return_to=` redirect from a mid-
// session 401 — that guard is RFC-0009's shell. No logout control (RFC-0009).

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoogleLogo } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  collectFlowMessages,
  fetchExistingFlow,
  fetchLoginFlow,
  findGoogleOidcNode,
  getCsrfToken,
  LoginFlowError,
  sanitizeReturnTo,
  type KratosLoginFlow,
} from '@/lib/auth-api';

type Status = 'loading' | 'ready' | 'error';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  // A `flow` param means Kratos redirected back here (e.g. carrying an
  // out-of-domain rejection); re-read that flow so its messages are shown.
  const flowId = searchParams.get('flow');
  const returnTo = sanitizeReturnTo(searchParams.get('return_to'));

  const [status, setStatus] = useState<Status>('loading');
  const [flow, setFlow] = useState<KratosLoginFlow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const next = flowId
        ? await fetchExistingFlow(flowId)
        : await fetchLoginFlow(returnTo);
      setFlow(next);
      setStatus('ready');
    } catch (err) {
      setError(
        err instanceof LoginFlowError
          ? err.message
          : 'Something went wrong starting sign-in. Please try again.',
      );
      setStatus('error');
    }
  }, [flowId, returnTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const googleNode = flow ? findGoogleOidcNode(flow) : undefined;
  const csrfToken = flow ? getCsrfToken(flow) : undefined;
  const messages = flow ? collectFlowMessages(flow) : [];

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-8 shadow-[var(--c-shadow-md)]">
        <div className="mb-6 flex flex-col gap-1.5 text-center">
          <h1 className="text-lg font-semibold text-text">Documentation Portal</h1>
          <p className="text-[13px] leading-relaxed text-muted">
            Sign in with your organization Google account to continue.
          </p>
        </div>

        {/* Flow messages (e.g. an out-of-domain rejection surfaced by Kratos). */}
        {messages.length > 0 && (
          <div className="mb-4 flex flex-col gap-2">
            {messages.map((m, i) => (
              <div
                key={m.id ?? i}
                role="alert"
                className={
                  m.type === 'error'
                    ? 'rounded-lg border border-[rgba(255,92,92,0.2)] bg-error-bg px-3 py-2 text-[13px] text-badge-error-text'
                    : 'rounded-lg border border-border bg-accent-faint px-3 py-2 text-[13px] text-muted'
                }
              >
                {m.text}
              </div>
            ))}
          </div>
        )}

        {status === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-4 text-[13px] text-muted">
            <span className="h-3.5 w-3.5 shrink-0 animate-spin-slow rounded-full border-2 border-current border-t-transparent" />
            Loading sign-in…
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col gap-4">
            <div
              role="alert"
              className="rounded-lg border border-[rgba(255,92,92,0.2)] bg-error-bg px-3 py-2 text-[13px] text-badge-error-text"
            >
              {error}
            </div>
            <Button size="md" className="min-h-[44px]" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        )}

        {status === 'ready' &&
          (googleNode ? (
            // Real top-level POST navigation — NOT fetch — so the browser follows
            // Kratos' OIDC redirect to Google and back with the session cookie.
            <form method="POST" action={flow?.ui.action}>
              {csrfToken !== undefined && (
                <input type="hidden" name="csrf_token" value={csrfToken} />
              )}
              <Button
                type="submit"
                size="md"
                name={googleNode.attributes.name}
                value={String(googleNode.attributes.value)}
                className="min-h-[44px] w-full"
              >
                <GoogleLogo size={18} weight="bold" />
                Continue with Google
              </Button>
            </form>
          ) : (
            <div
              role="alert"
              className="rounded-lg border border-[rgba(255,92,92,0.2)] bg-error-bg px-3 py-2 text-[13px] text-badge-error-text"
            >
              Google sign-in is unavailable right now. Please try again later.
            </div>
          ))}
      </div>
    </div>
  );
}
